import { useState } from "react";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { StringHelper, ValidationHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { UserDto } from "../../../services/dto";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import DisplayData from "../../common/DisplayData/DisplayData";
import { ViewProps } from "./props";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";

interface UserFormProps {
    data?: UserDto | undefined;
    show: boolean;
    lookups: ViewProps.UserLookupProps | undefined;
    onHide: () => void;
    onSubmit: (values: UserDto) => DrawerFormSaveResult;
    isLoading: boolean;
}

const validationSchema = yup.object().shape({
    firstName: yup.string().required("First Name is required."),
    lastName: yup.string().required("Last Name is required."),
    email: yup.string().required("Email is required.").email("Invalid email."),
    phoneNumber: ValidationHelper.yupValidatePhone(),
    roleId: yup.string().required("Role is required.")
        .test(
            "not-empty",
            "Role is required",
            (value) => value !== Constants.emptyGuid
        ),
    communityId: yup.string().when("roleId", ([roleId], schema) => {
        if (roleId === ViewProps.CommunityAdminRole.toLowerCase()) {
            return yup.string()
                .required("Community is required.")
                .test(
                    "not-empty",
                    "Community is required.",
                    (value) => value !== Constants.emptyGuid
                );
        }
        else {
            return yup.string().notRequired();
        }
        return schema;
    }),
});

export const UserForm = (props: UserFormProps) => {
    const { communityId } = useParams();
    const title: string =
        StringHelper.whenNullOrEmpty(props.data?.id, Constants.emptyGuid) ===
            Constants.emptyGuid
            ? "New User"
            : "Edit User";

    const onSubmit = (data: UserDto) => {
        return Promise.resolve(props.onSubmit(data)).then((result) => {
        if (hasClientErrorOperationStatusCode(result)) {
            return;
        }

        props.onHide();
    });
    };

    const isNew = !props.data?.id || props.data?.id === Constants.emptyGuid;

    const roleName = LookupDto.getLookupById(props.lookups?.roles, props.data?.roleId)?.display;
    const communityName = LookupDto.getLookupById(props.lookups?.communities, props.data?.communityId)?.display;
    const [showCommunity, setShowCommunity] = useState<boolean>(false);

    const onRoleChange = (val: string | undefined) => setShowCommunity(val?.toLowerCase() === ViewProps.CommunityAdminRole.toLowerCase() && communityId === undefined);

    return (
        <Drawer.Form
            className="w-30"
            show={props.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props.onHide}
            title={title}
            initialValues={props.data ?? {}}
            validationSchema={validationSchema}
            on_Submit={onSubmit}
            enableReinitialize
        >
            <Form.PanelSection iconClassName="bi bi-person" title="1. User Details">
                <Form.Text
                    name="firstName"
                    placeholder="First Name"
                    label="First Name"
                    isLoading={props.isLoading}
                />
                <Form.Text
                    name="lastName"
                    placeholder="Last Name"
                    label="Last Name"
                    isLoading={props.isLoading}
                />
                {isNew ?
                    <Form.Text
                        name="email"
                        placeholder="Email"
                        label="Email"
                        isLoading={props.isLoading}
                    />
                    : <DisplayData text="Email" importedData={props.data?.email} />
                }
                <Form.Phone
                    name="phoneNumber"
                    placeholder="Phone Number"
                    label="Phone Number"
                    isLoading={props.isLoading}
                />
            </Form.PanelSection>

            <Form.PanelSection
                iconClassName="bi bi-shield-lock"
                title="2. Access"
                style={{ marginTop: 16 }}
            >
                {isNew ?
                    <>
                        <Form.Dropdown
                            name={"roleId"}
                            label={"Role"}
                            isLoading={props.isLoading}
                            blankOption={{ key: Constants.emptyGuid, display: '--Select Role--', value: Constants.emptyGuid }}
                            options={LookupDto.mapToOptions(props.lookups?.roles, communityId ? [ViewProps.SuperAdminRole] : undefined)}
                            onChange={(e: any) => { onRoleChange(e.target.value); }}
                        />
                        {showCommunity && <Form.Dropdown
                            name={"communityId"}
                            label={"Community"}
                            isLoading={props.isLoading}
                            blankOption={{ key: Constants.emptyGuid, display: '--Select Community--', value: Constants.emptyGuid }}
                            options={LookupDto.mapToOptions(props.lookups?.communities)}
                        />}
                    </>
                    :
                    <>
                        <DisplayData text="Role" importedData={roleName} />
                        {communityName && <DisplayData text="Community" importedData={communityName} />}
                    </>
                }
            </Form.PanelSection>
        </Drawer.Form>
    );
}
